import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { UserApp } from '../services/api';

interface DeleteAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: UserApp;
  onConfirm: () => Promise<void>;
}

const DeleteAppDialog: React.FC<DeleteAppDialogProps> = ({
  open,
  onOpenChange,
  app,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  const expectedConfirmation = app.name;
  const isConfirmationValid = confirmationText === expectedConfirmation;

  const handleConfirm = async () => {
    if (!isConfirmationValid) return;
    
    setLoading(true);
    try {
      await onConfirm();
      setConfirmationText(''); // Reset on success
    } catch (error) {
      console.error('Error deleting app:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmationText(''); // Reset when closing
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-hidden p-0">
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-6 pt-6 pb-6 flex-shrink-0">
            <DialogHeader className="space-y-4">
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Delete Application
              </DialogTitle>
              <DialogDescription>
                This action will permanently delete <span className="font-medium">"{app.name}"</span> and all associated data.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="space-y-5">
              <Alert className="border-red-500/50 bg-red-500/10">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-red-600 dark:text-red-400">
                  <strong>Warning:</strong> This action is permanent and cannot be undone.
                </AlertDescription>
              </Alert>

              <div className="space-y-3 text-sm text-muted-foreground">
                <h4 className="font-medium text-foreground">What will be deleted:</h4>
                <ul className="space-y-2 list-disc list-inside pl-2">
                  <li>Application credentials (App ID & Secret Key)</li>
                  <li>All application configuration and settings</li>
                  <li>Any integrations using these credentials will stop working</li>
                  <li>This action cannot be reversed</li>
                </ul>
              </div>

              <div className="bg-muted p-4 rounded-lg border space-y-3">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                    App ID
                  </p>
                  <code className="text-sm font-mono break-all">
                    {app.id}
                  </code>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                    Created
                  </p>
                  <p className="text-sm">
                    {new Date(app.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="confirmation" className="text-sm font-medium">
                  Type <span className="font-mono">"{expectedConfirmation}"</span> to confirm deletion
                </Label>
                <Input
                  id="confirmation"
                  type="text"
                  placeholder={expectedConfirmation}
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading || !isConfirmationValid}
                variant="destructive"
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAppDialog;
